import { useCallback, useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap"
import parse from 'html-react-parser';
import { AppConstants } from "../../constants/AppConstants";
import classes from "./CourseFeedbackModal.module.css";
import Moment from "react-moment";
import { DateTimeUtil } from "../../utils/DateTimeUtil";
import { AuthorizedDataService } from "../../services/AuthorizedDataService";
import { SubmitFeedbackTextEditor } from "../shareCourseFeedback/SubmitFeedbackTextEditor";
import { StringUtils } from "../../utils/StringUtils";
import { BiLoaderAlt, BiPencil } from "react-icons/bi";
import { AiFillDelete, AiOutlineCheckCircle, AiOutlineExclamationCircle } from "react-icons/ai";
import modalClass from "../cards/CompetitionsCards.module.css";
import { SuccessMessage } from "../../constants/SuccessMessage";

export const CourseFeedbackModal = ({ closeFeedbackModal, learningActivityDetails, learnerDetails, modalHeading, feedbackHistoryHeading, mode }) => {
    const [showSpinner, setShowSpinner] = useState(AppConstants.trueText);
    const [feedbackHistory, setFeedbackHistory] = useState([]);
    const [submitMessage, setSubmitMessage] = useState(AppConstants.emptyString);
    const [selectedRemark, setSelectedRemark] = useState(AppConstants.emptyString);
    const [updatingRemark, setUpdatingRemark] = useState(AppConstants.emptyString);
    const [updateWarning, setUpdateWarning] = useState(AppConstants.emptyString);
    const [feedbackUpdateMessage, setFeedbackUpdateMessage] = useState(AppConstants.emptyString);
    const [deleteSpinner, setDeleteSpinner] = useState(AppConstants.falseText);
    const [showModalSuccess, setShowModalSuccess] = useState(AppConstants.falseText);
    const [updateMode, setUpdateMode] = useState(AppConstants.emptyString);
    const timeZone = DateTimeUtil.getPreferredTimeZoneForUser();

    useEffect(() => {
        let timer = setTimeout(() => {
            setSubmitMessage(AppConstants.emptyString);
        }, AppConstants.messageDisappearTime);
        return () => { clearTimeout(timer) };
    }, [submitMessage]);

    const fetchFeedbackHistory = useCallback(async () => {
        const params = {
            learningActivityUniqueId: learningActivityDetails.learningActivityUniqueId,
            learningActivityType: AppConstants.learningActivitiesTypes.course,
            learnerEmail: encodeURIComponent(learnerDetails[0].studentEmail)
        }
        let url = StringUtils.createURLWithParams(AppConstants.getCourseFeedbackForLearnerAPI, params);
        let response = await AuthorizedDataService.getRequest(url, AppConstants.emptyString, AppConstants.emptyString);
        if (response) {
            setFeedbackHistory(response);
            setShowSpinner(AppConstants.falseText);
        } else {
            setShowSpinner(AppConstants.falseText);
            setFeedbackHistory(response);
        }
    }, [learnerDetails, learningActivityDetails?.learningActivityUniqueId])

    useEffect(() => {
        fetchFeedbackHistory();
    }, [fetchFeedbackHistory]);

    const refreshFeedbackHistory = async (mode) => {
        const params = {
            learningActivityUniqueId: learningActivityDetails.learningActivityUniqueId,
            learningActivityType: AppConstants.learningActivitiesTypes.course,
            learnerEmail: encodeURIComponent(learnerDetails[0].studentEmail)
        }
        let url = StringUtils.createURLWithParams(AppConstants.getCourseFeedbackForLearnerAPI, params);
        let response = await AuthorizedDataService.getRequest(url, AppConstants.emptyString, AppConstants.emptyString);
        if (response) {
            setFeedbackHistory(response);
            if (mode === AppConstants.actionModes.edit) {
                setSelectedRemark(AppConstants.emptyString)
            }
        }
    }

    const closeEditForm = () => {
        setSelectedRemark(AppConstants.emptyString);
        setUpdateMode(AppConstants.emptyString)
    };

    const deletedSelectedFeedback = () => {
        setUpdateMode(AppConstants.actionModes.delete);
        setUpdateWarning(AppConstants.deleteFeedbackConfirmation);
    };

    const removeFeedback = async () => {
        setDeleteSpinner(AppConstants.trueText);
        let url = AppConstants.submitFeedbackAPI;
        let data = {
            learningActivityUniqueId: learningActivityDetails.learningActivityUniqueId,
            learningActivityType: AppConstants.learningActivitiesTypes.course,
            feedbackRemarks: updatingRemark.response,
            learningActivityId: learningActivityDetails.learningActivityId,
            learningActivityName: learningActivityDetails.learningActivityTitle,
            feedbackId: updatingRemark.id,
            feedbackUniqueId: updatingRemark.uniqueId,
            studentFeedbackInfo: learnerDetails.map(student =>
            ({
                deleteFlag: AppConstants.yText,
                studentEmail: student.studentEmail
            }))
        };

        const response = await AuthorizedDataService.postRequest(url, data, AppConstants.emptyString, AppConstants.emptyString);
        if (response) {
            setDeleteSpinner(AppConstants.falseText);
            setFeedbackUpdateMessage(SuccessMessage.courseFeedbackSuccessfullyDeleted);
            refreshFeedbackHistory(AppConstants.actionModes.edit);
            setShowModalSuccess(AppConstants.trueText);
        } else {
            setDeleteSpinner(AppConstants.falseText);
            setFeedbackUpdateMessage("");
        }
    };

    const closeDeleteFeedbackModal = () => {
        setFeedbackUpdateMessage(AppConstants.emptyString);
        setUpdatingRemark(AppConstants.emptyString);
        setUpdateWarning(AppConstants.emptyString);
        setShowModalSuccess(AppConstants.falseText);
    };

    const onClickEditFeedback = (feedback) => {
        setUpdateMode(AppConstants.actionModes.edit);
        if (feedback.totalFeedbackCount > 1) {
            setUpdatingRemark(feedback);
            setUpdateWarning(AppConstants.editFeedbackConfirmation);
        } else {
            setSelectedRemark(feedback);
            setSubmitMessage(AppConstants.falseText);
        }
    };

    const setEditFeedbackDetails = () => {
        setSelectedRemark(updatingRemark);
        setSubmitMessage(AppConstants.emptyString);
        setFeedbackUpdateMessage(AppConstants.falseText);
        setUpdatingRemark(AppConstants.emptyString);
        setUpdateWarning(AppConstants.emptyString);
        setShowModalSuccess(AppConstants.falseText);
    }

    return (
        <Modal size="xl" show={true} onHide={closeFeedbackModal} backdrop="static" keyboard={false} centered>
            {modalHeading && !showSpinner && <Modal.Header closeButton>
                {mode !== AppConstants.actionModes.view && <h1 className={classes.heading}>{modalHeading}</h1>}
                {mode === AppConstants.actionModes.view && <div className={classes.profileImageSection}>
                    {<div className={classes.reviewInitials}><img src={learningActivityDetails.userImagePath} className={classes.profilePicture} alt="user_profile" /></div>}
                    <div className={classes}>
                        <p className={classes.reviewerName}>{learningActivityDetails.organizedBy}</p>
                        <p className={classes.activityName}>Feedback for {learningActivityDetails.learningActivityName}</p>
                    </div>
                </div>}
            </Modal.Header>}
            {updateWarning === AppConstants.emptyString && !updatingRemark.id && <Modal.Body>
                {showSpinner ? <p className={classes.loaderIcon}><BiLoaderAlt className="spinner" /></p> : <div className={classes.modalBody}>
                    {!selectedRemark && mode !== AppConstants.actionModes.view && <>
                        <SubmitFeedbackTextEditor learningActivityDetails={learningActivityDetails} learnerDetails={learnerDetails} placeholder="Type Your Feedback" refreshFeedbackHistory={refreshFeedbackHistory} />
                    </>}
                    {feedbackHistory.length > 0 &&
                        <div>
                            <h2 className={classes.previousRemarksHeading}>{feedbackHistoryHeading}</h2>
                            <div className={classes.responseContainer}>
                                {feedbackHistory.map((feedback, index) => (
                                    <div key={index}>
                                        {selectedRemark?.uniqueId && selectedRemark.uniqueId === feedback.uniqueId ? <>
                                            <SubmitFeedbackTextEditor learningActivityDetails={learningActivityDetails} learnerDetails={learnerDetails} placeholder="Type Your Feedback" selectedResponse={selectedRemark} closeEditForm={closeEditForm} refreshFeedbackHistory={refreshFeedbackHistory} />
                                        </> :
                                            <div className={classes.responseCardContainer}>


                                                {mode !== AppConstants.actionModes.view && <div className={classes.editIcon}><BiPencil onClick={() => onClickEditFeedback(feedback)} /></div>}
                                                {mode !== AppConstants.actionModes.view && <div className={classes.deleteIcon}><AiFillDelete onClick={() => { setUpdatingRemark(feedback); setSubmitMessage(AppConstants.falseText); deletedSelectedFeedback(); }} /></div>}
                                                <div className={`${classes.responseCard} ql-editor`}>{parse(feedback?.response)}
                                                </div>
                                                {feedback.respondedOn && feedback.responseUpdatedOn && (
                                                    <span className={classes.editedText}>Edited</span>
                                                )}
                                                <p className={classes.responseDateAndTime}><Moment format={AppConstants.twelveHoursTimeFormat} tz={timeZone}>{feedback.respondedOn.replace(' ', 'T') + 'Z'}</Moment> | <Moment format={AppConstants.competitionDatesNewDateFormat} tz={timeZone}>{feedback.respondedOn.replace(' ', 'T') + 'Z'}</Moment> {timeZone === AppConstants.utcTimezone && `(${feedbackHistory.preferredTimezone})`}</p>
                                            </div>}
                                    </div>))}
                            </div>
                        </div>
                    }
                </div>}
            </Modal.Body>}
            {updateWarning !== AppConstants.emptyString && updatingRemark.id && <>
                <Modal.Body>
                    <div className={classes.warningModalbody}>
                        {!showModalSuccess && <div className={modalClass.modalErrorIcon}><AiOutlineExclamationCircle /></div>}
                        {showModalSuccess && <div className={modalClass.modalSuccessIcon}><AiOutlineCheckCircle /></div>}
                        <div className={modalClass.modalAlertText}>
                            <p className={classes.deleteMessage}>{feedbackUpdateMessage ? feedbackUpdateMessage : updateWarning}</p>
                        </div>
                        {!feedbackUpdateMessage && <div className={`${classes.responseCard} ql-editor`}>{parse(updatingRemark?.response)}</div>}
                    </div>
                </Modal.Body>
                <Modal.Footer className={modalClass.modalFooter}>
                    {feedbackUpdateMessage && <Button className={modalClass.modalCloseButton} onClick={closeDeleteFeedbackModal}>Close</Button>}
                    {!feedbackUpdateMessage && <Button className={modalClass.modalNoButton} onClick={() => { setUpdatingRemark(AppConstants.emptyString); setUpdateWarning(AppConstants.emptyString) }}>No</Button>}
                    {!feedbackUpdateMessage && <Button className={modalClass.modalYesButton} onClick={() => updateMode === AppConstants.actionModes.delete ? removeFeedback() : setEditFeedbackDetails()}>Yes {deleteSpinner && <BiLoaderAlt className="spinner" />}</Button>}
                </Modal.Footer>
            </>}
        </Modal>
    )
}